import type {AdminUser} from "./admin/types";
import {SESSION_IDLE_TTL_SEC} from "../sessionTTL";

const TOKEN_KEY = "cc_admin_token";
const TOKEN_COOKIE = "cc_admin_bearer";

function setAdminTokenCookie(token: string | null): void {
  if(typeof document === "undefined") return;
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  if(token) {
    document.cookie = `${TOKEN_COOKIE}=${encodeURIComponent(token)}; path=/; max-age=${SESSION_IDLE_TTL_SEC}; SameSite=Lax${secure}`;
  } else {
    document.cookie = `${TOKEN_COOKIE}=; path=/; max-age=0; SameSite=Lax${secure}`;
  }
}

export function getAdminToken(): string | null {
  if(typeof localStorage === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setAdminToken(token: string | null): void {
  if(typeof localStorage === "undefined") return;
  if(token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
  setAdminTokenCookie(token);
}

export function adminAuthHeaders(): Record<string, string> {
  const token = getAdminToken();
  return token ? {Authorization: `Bearer ${token}`} : {};
}

function b64urlDecode(input: string): string {
  let s = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4;
  if(pad) s += "=".repeat(4 - pad);
  return atob(s);
}

type AdminJwtPayload = {
  sub?: number;
  nume?: string;
  email?: string;
  rol?: number;
  exp?: number;
};

export function decodeAdminTokenPayload(token: string): AdminJwtPayload | null {
  try {
    const part = token.split(".")[1];
    if(!part) return null;
    const json = JSON.parse(b64urlDecode(part)) as AdminJwtPayload;
    return json && typeof json === "object" ? json : null;
  } catch {
    return null;
  }
}

export function decodeAdminTokenExp(token: string): number | null {
  const exp = decodeAdminTokenPayload(token)?.exp;
  return typeof exp === "number" ? exp : null;
}

export function isAdminTokenExpired(token: string | null = getAdminToken()): boolean {
  if(!token) return true;
  const exp = decodeAdminTokenExp(token);
  if(!exp) return true;
  return exp <= Math.floor(Date.now() / 1000);
}

/** Refresh when less than 24h remain (keeps sliding 7-day JWT). */
export function shouldRefreshAdminToken(token: string | null = getAdminToken()): boolean {
  if(!token) return true;
  const exp = decodeAdminTokenExp(token);
  if(!exp) return true;
  return exp - Math.floor(Date.now() / 1000) < 24 * 60 * 60;
}

export function adminUserFromToken(token: string): AdminUser | null {
  const payload = decodeAdminTokenPayload(token);
  if(!payload || isAdminTokenExpired(token)) return null;
  const id = Number(payload.sub ?? 0);
  if(id <= 0) return null;
  return {
    authenticated: true,
    id,
    nume: payload.nume ?? "",
    email: payload.email ?? "",
    rol: typeof payload.rol === "number" ? payload.rol : 2
  };
}
